import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface RAGChunk {
  text: string
  filename: string
  documentId: number | null
  score: number
  chunkIndex: number
}

export interface Message {
  id: number | string
  message: string
  streaming: boolean
  user: string
  ragChunks?: RAGChunk[]
  llm_provider?: string
  llm_model_name?: string
  created_at?: string
}

export interface TestMessageData {
  id: number
  message: string
  user: string
  created_at: string
  llm_provider?: string
  llm_model_name?: string
}

export interface ExistingTestChat {
  id: number
  name: string
  created_at: string
  temperature: number
  max_tokens: number
  history_limit: number
  llm_model: string
  message_count: number
  messages: TestMessageData[]
}

export const TEST_CHAT_ID = -1

const BASE_RIGHT = 16
const BASE_BOTTOM = 16
const BOX_WIDTH = 448
const BOX_SPACING = 16

export interface OpenChatBox {
  id: number
  chatData: ExistingTestChat | null
  isReadOnly: boolean
  isTestChat?: boolean
  position: { right: number; bottom: number }
  dimensions: { width: number; height: number }
}

interface TestChatState {
  messages: Message[]
  isConnected: boolean
  error: string | null
  isGenerating: boolean
  isTestChatOpen: boolean
  existingTestChats: ExistingTestChat[]
  openChatBoxes: OpenChatBox[]
}

const initialState: TestChatState = {
  messages: [],
  isConnected: false,
  error: null,
  isGenerating: false,
  isTestChatOpen: false,
  existingTestChats: [],
  openChatBoxes: [],
}

const testChatMessageSlice = createSlice({
  name: 'testChatMessages',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      const mergeMetadata = (target: Message) => {
        if (action.payload.created_at !== undefined) {
          target.created_at = action.payload.created_at
        }
        if (action.payload.llm_provider !== undefined) {
          target.llm_provider = action.payload.llm_provider
        }
        if (action.payload.llm_model_name !== undefined) {
          target.llm_model_name = action.payload.llm_model_name
        }
      }

      if (action.payload.streaming) {
        if (state.messages.length > 0) {
          const lastMessage = state.messages[state.messages.length - 1]
          if (lastMessage.streaming) {
            if (action.payload.message) {
              lastMessage.message += action.payload.message
            }
            if (action.payload.ragChunks !== undefined) {
              lastMessage.ragChunks = action.payload.ragChunks
            }
            mergeMetadata(lastMessage)
            return
          }
        }
        state.messages.push(action.payload)
      } else if (!action.payload.streaming) {
        if (
          state.messages.length > 0 &&
          state.messages[state.messages.length - 1].streaming
        ) {
          const lastMessage = state.messages[state.messages.length - 1]
          state.messages[state.messages.length - 1] = {
            ...lastMessage,
            ...action.payload,
            message: action.payload.message || lastMessage.message,
            streaming: false,
            ragChunks: action.payload.ragChunks ?? lastMessage.ragChunks,
          }
        } else {
          state.messages.push(action.payload)
        }
      } else {
        state.messages.push(action.payload)
      }
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    updateTestChatMessage: (state, action: PayloadAction<Message>) => {
      const messageIndex = state.messages.findIndex(
        (msg) => String(msg.id) === String(action.payload.id)
      )

      if (messageIndex !== -1) {
        const existing = state.messages[messageIndex]
        if (action.payload.message) {
          existing.message += action.payload.message
        }
        if (action.payload.streaming === false) {
          existing.streaming = false
        } else if (action.payload.streaming === true) {
          existing.streaming = true
        }
        if (action.payload.created_at !== undefined) {
          existing.created_at = action.payload.created_at
        }
        if (action.payload.llm_provider !== undefined) {
          existing.llm_provider = action.payload.llm_provider
        }
        if (action.payload.llm_model_name !== undefined) {
          existing.llm_model_name = action.payload.llm_model_name
        }
        if (action.payload.ragChunks !== undefined) {
          existing.ragChunks = action.payload.ragChunks
        }
      } else {
        state.messages.push(action.payload)
      }
    },
    clearMessages: (state) => {
      state.messages = []
    },
    setIsGenerating(state, action: PayloadAction<boolean>) {
      state.isGenerating = action.payload
    },
    setIsTestChatOpen(state, action: PayloadAction<boolean>) {
      state.isTestChatOpen = action.payload
    },
    setExistingTestChats(state, action: PayloadAction<ExistingTestChat[]>) {
      state.existingTestChats = action.payload
    },
    loadTestChatMessages(state, action: PayloadAction<ExistingTestChat>) {
      const formattedMessages: Message[] = action.payload.messages.map(
        (msg) => ({
          id: msg.id,
          message: msg.message,
          streaming: false,
          user: msg.user,
          created_at: msg.created_at,
          llm_provider: msg.llm_provider,
          llm_model_name: msg.llm_model_name,
        })
      )
      state.messages = formattedMessages
    },
    addOpenChatBox(
      state,
      action: PayloadAction<{ chatData: ExistingTestChat; isReadOnly: boolean }>
    ) {
      const { chatData, isReadOnly } = action.payload

      const existingChat = state.openChatBoxes.find(
        (chat) => chat.id === chatData.id
      )
      if (existingChat) return

      if (state.openChatBoxes.length >= 3) {
        const nonTestChatIndex = state.openChatBoxes.findIndex(
          (chat) => !chat.isTestChat
        )
        if (nonTestChatIndex !== -1) {
          state.openChatBoxes.splice(nonTestChatIndex, 1)
        } else {
          state.openChatBoxes.shift()
        }
      }

      const offsetX = state.openChatBoxes.length * (BOX_WIDTH + BOX_SPACING)

      const newChatBox: OpenChatBox = {
        id: chatData.id,
        chatData,
        isReadOnly,
        isTestChat: false,
        position: {
          right: BASE_RIGHT + offsetX,
          bottom: BASE_BOTTOM,
        },
        dimensions: {
          width: BOX_WIDTH,
          height: 512,
        },
      }

      state.openChatBoxes.push(newChatBox)
    },
    addTestChatBox(state) {
      const existingTestChat = state.openChatBoxes.find(
        (chat) => chat.isTestChat
      )
      if (existingTestChat) return

      if (state.openChatBoxes.length >= 3) {
        const nonTestChatIndex = state.openChatBoxes.findIndex(
          (chat) => !chat.isTestChat
        )
        if (nonTestChatIndex !== -1) {
          state.openChatBoxes.splice(nonTestChatIndex, 1)
        } else {
          return
        }
      }

      const offsetX = state.openChatBoxes.length * (BOX_WIDTH + BOX_SPACING)

      const testChatBox: OpenChatBox = {
        id: TEST_CHAT_ID,
        chatData: null,
        isReadOnly: false,
        isTestChat: true,
        position: {
          right: BASE_RIGHT + offsetX,
          bottom: BASE_BOTTOM,
        },
        dimensions: {
          width: BOX_WIDTH,
          height: 512,
        },
      }

      state.openChatBoxes.push(testChatBox)
    },
    removeTestChatBox(state) {
      state.openChatBoxes = state.openChatBoxes.filter(
        (chat) => !chat.isTestChat
      )
      state.openChatBoxes.forEach((chat, index) => {
        chat.position = {
          right: BASE_RIGHT + index * (BOX_WIDTH + BOX_SPACING),
          bottom: BASE_BOTTOM,
        }
      })
    },
    removeOpenChatBox(state, action: PayloadAction<number>) {
      state.openChatBoxes = state.openChatBoxes.filter(
        (chat) => chat.id !== action.payload
      )
      state.openChatBoxes.forEach((chat, index) => {
        chat.position = {
          right: BASE_RIGHT + index * (BOX_WIDTH + BOX_SPACING),
          bottom: BASE_BOTTOM,
        }
      })
    },
    updateChatBoxPosition(
      state,
      action: PayloadAction<{
        id: number
        position: { right: number; bottom: number }
      }>
    ) {
      const chatBox = state.openChatBoxes.find(
        (chat) => chat.id === action.payload.id
      )
      if (chatBox) {
        chatBox.position = action.payload.position
      }
    },
    updateChatBoxDimensions(
      state,
      action: PayloadAction<{
        id: number
        dimensions: { width: number; height: number }
      }>
    ) {
      const chatBox = state.openChatBoxes.find(
        (chat) => chat.id === action.payload.id
      )
      if (chatBox) {
        chatBox.dimensions = action.payload.dimensions
      }
    },
    clearAllOpenChatBoxes(state) {
      state.openChatBoxes = []
    },
  },
})

export const {
  setConnectionStatus,
  addMessage,
  updateTestChatMessage,
  setMessages,
  setError,
  clearMessages,
  setIsGenerating,
  setIsTestChatOpen,
  setExistingTestChats,
  loadTestChatMessages,
  addOpenChatBox,
  addTestChatBox,
  removeTestChatBox,
  removeOpenChatBox,
  updateChatBoxPosition,
  updateChatBoxDimensions,
  clearAllOpenChatBoxes,
} = testChatMessageSlice.actions

export default testChatMessageSlice.reducer
